import { useMemo } from 'react'

import CurrencyIcon from '@/assets/icons/icon-currency.svg'
import { LineChart } from '@/components/charts/line-chart.tsx'
import { Card, CardContent } from '@/components/ui/card.tsx'
import type { AccountingSnapshotItem } from '@/modules/accounting/types.ts'
import { roundAmount } from '@/utils/number.ts'

interface Props {
  accountingSnapshotItems: AccountingSnapshotItem[]
  showCents?: boolean
}

export const AccountingProfitChart = ({
  accountingSnapshotItems,
  showCents = true,
}: Props) => {
  const { totalRevenue, totalProfit, labels, revenueData, profitData } =
    useMemo(() => {
      return accountingSnapshotItems.reduce(
        (acc, item) => {
          acc.totalRevenue += item.totalRevenue
          acc.totalProfit += item.netProfit
          acc.labels.push(item.date)
          acc.revenueData.push(item.totalRevenue)
          acc.profitData.push(item.netProfit)

          return acc
        },
        {
          totalRevenue: 0,
          totalProfit: 0,
          labels: [] as string[],
          revenueData: [] as number[],
          profitData: [] as number[],
        },
      )
    }, [accountingSnapshotItems])

  const data = useMemo(
    () => ({
      labels,
      datasets: [
        {
          label: 'Revenue',
          data: revenueData,
          backgroundColor: '#1E84E3',
          borderColor: '#1E84E3',
        },
        {
          label: 'Profit',
          data: profitData,
          backgroundColor: '#28A745',
          borderColor: '#28A745',
        },
      ],
    }),
    [labels, revenueData, profitData],
  )

  return (
    <Card>
      <CardContent className="mt-6">
        <div className="grid grid-cols-2 gap-3">
          <div className="border-primary-500 bg-primary-50 dark:bg-primary-900 flex items-center justify-between rounded border-l-4 px-4 py-2">
            <div>
              <span className="text-text-default font-medium">
                {roundAmount(totalRevenue, showCents)}
              </span>
              <p className="text-secondary-300 text-sm">Total Revenue</p>
            </div>
            <div className="bg-primary-100 dark:bg-primary-800 rounded-lg p-2">
              <CurrencyIcon />
            </div>
          </div>

          <div className="border-success-default flex items-center justify-between rounded border-l-4 bg-[#E8FFED] px-4 py-2 dark:bg-[#04280C]">
            <div>
              <span className="text-text-default font-medium">
                {roundAmount(totalProfit, showCents)}
              </span>
              <p className="text-secondary-300 text-sm">Net Profit</p>
            </div>
            <div className="bg-success-light rounded-lg p-2 dark:bg-[#0B4D1A]">
              <CurrencyIcon className="fill-success-default" />
            </div>
          </div>
        </div>

        <div className="mt-5">
          <LineChart
            data={data}
            toggleOptions={{ isTooltipCurrency: true }}
            showCents={showCents}
          />
        </div>
      </CardContent>
    </Card>
  )
}
